import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {PagesSelector} from '../../Selectors';
import {CoreDoms, Viewers} from '@taopix/taopix-core';
import {PageInterface, ProductLayout} from '@taopix/taopix-core/dist/types/types';
import Thumbnail from './Thumbnail';
import classNames from 'classnames';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  // eslint-disable-next-line node/no-extraneous-import
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {restrictToParentElement} from '@dnd-kit/modifiers';
import {useExperience} from '../Experience/ExperienceProvider';
import {useTaopix} from '../Context/TaopixContext';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {useUserFeedback} from '../Context/UserFeedback/UserFeedbackContext';
import {UserFeedbackActions} from '../Context/UserFeedback/UserFeedbackActionTypes';

export type PaginationProps = {
  layout: ProductLayout;
  onChangePage: (pageNumber: number) => void;
};

const Pagination = ({layout, onChangePage}: PaginationProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const {experience} = useExperience();
  const userFeedback = useUserFeedback();
  const pages = useSelector(PagesSelector);
  const containerRef = useRef<HTMLDivElement>(null);

  const [ordering, setOrdering] = useState(
    pages.reduce((acc, curr, i) => {
      if (!acc[curr.pagenumber]) {
        acc[curr.pagenumber] = i;
      }
      return acc;
    }, {})
  );

  // sort the pages based on their css sort
  const sortedPages = useMemo(() => {
    return pages.reduce<PageInterface[]>((acc, page) => {
      acc.splice(ordering[page.pagenumber], 0, page);
      return acc;
    }, []);
  }, [pages, ordering]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      // Require the mouse to move by 10 pixels before activating
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      // Press delay of 250ms, with tolerance of 5px of movement
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setOrdering(
      pages.reduce((acc, curr, i) => {
        if (!acc[curr.pagenumber]) {
          acc[curr.pagenumber] = i;
        }
        return acc;
      }, {})
    );
  }, [pages]);

  const transformScroll = (event: WheelEvent) => {
    if (!event.deltaY) {
      return;
    }
    const element = event.currentTarget as HTMLElement | null;
    if (element) {
      element.scrollLeft += event.deltaY;
    }
  };

  useEffect(() => {
    const scrollElement = containerRef.current;
    scrollElement?.addEventListener('wheel', transformScroll, {passive: true});

    return () => {
      scrollElement?.removeEventListener('wheel', transformScroll);
    };
  }, []);

  const onDragEnd = useCallback(
    (event: DragEndEvent): void => {
      const {active, over} = event;

      if (active.id === over?.id) {
        return void 0;
      }

      const from = pages.find(item => item.id === active.id);
      const to = pages.find(item => item.id === over?.id);

      if (!from || !to) {
        return void 0;
      }

      const oldIndex = pages.findIndex(item => item.id === active.id);
      const newIndex = pages.findIndex(item => item.id === over?.id);

      // check to see if we can drop the item

      setOrdering(
        arrayMove(pages, oldIndex, newIndex).reduce((acc, curr, i) => {
          if (!acc[curr.pagenumber]) {
            acc[curr.pagenumber] = i;
          }
          return acc;
        }, {})
      );

      app.core
        .get('stateManager')
        .call('movepage', {
          currentPage: ui.state.pageNumber,
          page: from.pagenumber,
          before: to.pagenumber,
        })
        .then(() => {
          setOrdering(
            pages.reduce((acc, curr, i) => {
              if (!acc[curr.pagenumber]) {
                acc[curr.pagenumber] = i;
              }
              return acc;
            }, {})
          );
        })
        .catch(error => {
          const message = error?.message ? error.message : error;
          userFeedback.dispatch(
            UserFeedbackActions.setErrorUserFeedback({
              title: '',
              message,
              showHeader: true,
            })
          );
        });
    },
    [pages]
  );

  const tpxCoreAttrs = {
    [CoreDoms.ProjectViewerAttr]: Viewers.Secondary,
    [CoreDoms.MaxWidthAttr]: '150', // The maximum width of the page thumbnail as a percentage of the project viewer height
    [CoreDoms.MaxHeightAttr]: '60', // The maximum height of the page thumbnail as a percentage of the project viewer height
    [CoreDoms.ResizeThresholdAttr]: '10',
    [CoreDoms.ListDirectionAttr]: 'row',
  };

  const tpxPagesAttrs = {
    [CoreDoms.ProjectPagesAttr]: 'true',
  };

  const projectViewerClassNames = classNames([
    'flex',
    'h-[120rem]',
    'w-full',
    'pt-md',
    'px-sm',
    'pb-xs',
    'overflow-x-auto',
    'items-end',
  ]);

  // Classes for the UL
  const pageListClasses = classNames(['flex']);

  if (!experience.EDITOR.workArea.singlePage.pageThumbnailBar || 1 === layout.pagecount) {
    return <></>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      modifiers={[restrictToParentElement]}
    >
      <SortableContext items={sortedPages} strategy={horizontalListSortingStrategy}>
        <div
          {...tpxCoreAttrs}
          className={projectViewerClassNames}
          ref={containerRef}
          id={'projectViewerThumbs'}
        >
          <ul {...tpxPagesAttrs} className={pageListClasses}>
            {sortedPages.map((page, index) => {
              // Requires a compound key to detect if the page has changed position, via add or delete pages, etc
              // Adding the layout code to the key causes the thumbs to re-render on change product. DO NOT REMOVE
              return (
                <Thumbnail
                  layout={layout}
                  page={page}
                  key={`thumbnail-${layout.code}-${page.ref}-${page.pagenumber}`}
                  onChangePage={onChangePage}
                  order={ordering[page.pagenumber]}
                />
              );
            })}
          </ul>
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default Pagination;
